import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { FamilyMemberFormFields } from "./FamilyMemberFormFields";
import { familyMemberFormSchema, FamilyMemberFormValues } from "./types";

interface AddFamilyMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  familyGroupId: string;
}

export function AddFamilyMemberDialog({ 
  open, 
  onOpenChange,
  familyGroupId 
}: AddFamilyMemberDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  
  const form = useForm<FamilyMemberFormValues>({
    resolver: zodResolver(familyMemberFormSchema),
    defaultValues: {
      email: "",
      full_name: "",
      family_role: "spouse",
    },
  });

  const onSubmit = async (values: FamilyMemberFormValues) => {
    try {
      setIsSubmitting(true);

      // Check if user already exists
      const { data: existingUser, error: searchError } = await supabase
        .from('profiles')
        .select('id, family_group_id')
        .eq('email', values.email)
        .single();

      if (searchError && searchError.code !== 'PGRST116') {
        throw searchError;
      }

      if (existingUser) {
        if (existingUser.family_group_id) {
          toast({
            title: "User already in a family group",
            description: "This user is already part of another family group.",
            variant: "destructive",
          });
          return;
        }

        // Update existing user's profile
        const { error: updateError } = await supabase
          .from('profiles')
          .update({
            family_group_id: familyGroupId,
            family_role: values.family_role,
            relationship_verified: false
          })
          .eq('id', existingUser.id);

        if (updateError) throw updateError;
      } else {
        // Generate a unique client number
        const clientNumber = `${new Date().getFullYear()}-${Math.random().toString(36).substring(7)}`;
        
        // Create new profile with a generated UUID
        const newId = crypto.randomUUID();
        
        const { error: insertError } = await supabase
          .from('profiles')
          .insert({
            id: newId,
            email: values.email,
            full_name: values.full_name,
            family_role: values.family_role,
            family_group_id: familyGroupId,
            relationship_verified: false,
            client_number: clientNumber
          });

        if (insertError) throw insertError;
      }

      toast({
        title: "Family member added",
        description: "An invitation has been sent to their email.",
      });
      
      form.reset();
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error adding family member:', error);
      toast({
        title: "Error",
        description: "Failed to add family member. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Family Member</DialogTitle>
          <DialogDescription>
            Add a family member to your account. They will receive an invitation email.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FamilyMemberFormFields form={form} />

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Add Member
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}